import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  Paper,
  Container,
  Link,
} from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import WindowIcon from '@mui/icons-material/Window';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import athenaLogo from '../assets/athena-logo-blue.png';
import npsfLogo from '../assets/npsf-logo.png';
import { getAthenaDownloadUrl } from '../api/athena4p-api';

const AthenaDownload: React.FC = () => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState<'windows' | 'mac' | null>(
    null
  );
  const [downloadError, setDownloadError] = useState('');

  const handleDownload = async (platform: 'windows-latest' | 'mac-latest') => {
    const downloadType = platform === 'windows-latest' ? 'windows' : 'mac';
    setIsDownloading(downloadType);
    setDownloadError('');

    try {
      const response = await getAthenaDownloadUrl(platform);

      if (response.data?.success && response.data?.downloadUrl) {
        window.location.href = response.data.downloadUrl;
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setDownloadError(response.data?.error || 'Failed to get download URL');
      }
    } catch (err: any) {
      console.error('Download error:', err);
      setDownloadError(
        err.response?.data?.error ||
          err.message ||
          'Failed to initiate download'
      );
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - var(--header-height, 76px))',
        background:
          'linear-gradient(135deg, #002855 0%, #004080 50%, #0066cc 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth='sm'>
        <Paper
          elevation={6}
          sx={{
            p: 5,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Stack spacing={4} alignItems='center'>
            {/* Logos */}
            <Stack
              direction='row'
              spacing={3}
              alignItems='center'
              justifyContent='center'
            >
              <RouterLink to='/' style={{ display: 'inline-flex' }}>
                <img
                  src={athenaLogo}
                  alt='Athena Logo'
                  style={{ height: 50, width: 'auto' }}
                />
              </RouterLink>
              <Box
                sx={{
                  width: 1,
                  height: 50,
                  backgroundColor: '#e0e0e0',
                }}
              />
              <img
                src={npsfLogo}
                alt='NPS Foundation Logo'
                style={{ height: 60, width: 'auto' }}
              />
            </Stack>

            {/* Title and Description */}
            <Box>
              <Typography
                variant='h4'
                component='h1'
                sx={{
                  fontWeight: 700,
                  color: '#002855',
                  mb: 1,
                }}
              >
                Download Athena
              </Typography>
              <Typography
                variant='body1'
                color='text.secondary'
                sx={{ maxWidth: 400, mx: 'auto' }}
              >
                Get the Athena desktop application for your platform and start
                exploring the knowledge network.
              </Typography>
            </Box>

            {/* Error Alert */}
            {downloadError && (
              <Alert severity='error' sx={{ width: '100%' }}>
                {downloadError}
              </Alert>
            )}

            {/* Download Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: '100%', justifyContent: 'center' }}
            >
              <Button
                variant='contained'
                size='large'
                startIcon={<WindowIcon />}
                onClick={() => handleDownload('windows-latest')}
                disabled={isDownloading !== null}
                sx={{
                  backgroundColor: '#0078d4',
                  '&:hover': {
                    backgroundColor: '#106ebe',
                  },
                  minWidth: 200,
                  py: 1.5,
                }}
              >
                {isDownloading === 'windows' ? 'Preparing...' : 'Windows'}
              </Button>
              <Button
                variant='contained'
                size='large'
                startIcon={<AppleIcon />}
                onClick={() => handleDownload('mac-latest')}
                disabled={isDownloading !== null}
                sx={{
                  backgroundColor: '#333',
                  '&:hover': {
                    backgroundColor: '#555',
                  },
                  minWidth: 200,
                  py: 1.5,
                }}
              >
                {isDownloading === 'mac' ? 'Preparing...' : 'macOS'}
              </Button>
            </Stack>

            {/* Registration Notice */}
            <Alert
              severity='info'
              icon={<InfoOutlinedIcon />}
              sx={{
                width: '100%',
                backgroundColor: '#e3f2fd',
                '& .MuiAlert-icon': {
                  color: '#1976d2',
                },
              }}
            >
              <Typography variant='body2'>
                You must be a registered Athena user to use this application.{' '}
                <Link
                  href='/request-account'
                  sx={{
                    color: '#002855',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  Request an account
                </Link>
              </Typography>
            </Alert>

            {/* Support */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                width: '100%',
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                Need help?{' '}
                <a
                  href='mailto:athena@npsfoundation.org'
                  style={{
                    color: '#002855',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  athena@npsfoundation.org
                </a>
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default AthenaDownload;
